import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { fetchRegionalUserContext } from "../utils/backend/pdxBackendClient";

/**
 * Loads the authenticated user's backend account context from the regional
 * API gateway.
 *
 * This hook is intentionally read-only and non-blocking so pages can surface
 * backend auth/bootstrap state without coupling core page rendering to the
 * request lifecycle.
 *
 * @param {{
 *   enabled?: boolean,
 *   fetchImpl?: typeof fetch
 * }} [options={}] - Hook options and test overrides.
 * @returns {{
 *   loading: boolean,
 *   data: unknown,
 *   error: Error | null,
 *   reload: () => Promise<unknown | null>,
 *   applyData: (updater: unknown | ((currentData: unknown) => unknown)) => void
 * }} Backend user-context request state.
 */
export function useRegionalUserContext(options = {}) {
  const { enabled = true, fetchImpl } = options;
  const { user, authVersion } = useAuth();
  const [requestVersion, setRequestVersion] = useState(0);
  const pendingReloadsRef = useRef([]);
  const settledRequestVersionRef = useRef(0);
  const [state, setState] = useState({
    loading: false,
    data: null,
    error: null,
    settledRequestVersion: 0,
  });

  const reload = useCallback(() => {
    if (!enabled || !user?.uid) {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      pendingReloadsRef.current.push({ resolve, reject });
      setRequestVersion((currentValue) => currentValue + 1);
    });
  }, [enabled, user?.uid]);

  const applyData = useCallback((updater) => {
    setState((currentState) => {
      const nextData =
        typeof updater === "function"
          ? updater(currentState.data)
          : updater;

      return {
        ...currentState,
        data: nextData,
        error: null,
      };
    });
  }, []);

  useEffect(() => {
    if (!enabled || !user?.uid) {
      settledRequestVersionRef.current += 1;
      setState({
        loading: false,
        data: null,
        error: null,
        settledRequestVersion: settledRequestVersionRef.current,
      });
      return;
    }

    let cancelled = false;

    setState((previous) => ({
      ...previous,
      // Keep already-verified backend context mounted while silent token
      // refreshes trigger a background revalidation.
      loading: previous.data ? false : true,
      error: null,
    }));

    fetchRegionalUserContext({ fetchImpl })
      .then((data) => {
        if (cancelled) return;

        settledRequestVersionRef.current += 1;
        setState({
          loading: false,
          data,
          error: null,
          settledRequestVersion: settledRequestVersionRef.current,
        });
      })
      .catch((error) => {
        if (cancelled) return;

        settledRequestVersionRef.current += 1;
        setState({
          loading: false,
          data: null,
          error,
          settledRequestVersion: settledRequestVersionRef.current,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [authVersion, enabled, fetchImpl, requestVersion, user?.uid]);

  useEffect(() => {
    if (pendingReloadsRef.current.length === 0) {
      return;
    }

    const pendingReloads = pendingReloadsRef.current;
    pendingReloadsRef.current = [];

    for (const { resolve, reject } of pendingReloads) {
      if (state.error) {
        reject(state.error);
      } else {
        resolve(state.data);
      }
    }
  }, [state.data, state.error, state.settledRequestVersion]);

  return {
    ...state,
    reload,
    applyData,
  };
}

export default useRegionalUserContext;
